import { useState, useEffect } from 'react';
import { getUserInfo, getCachedUserInfo } from '../store/userInfoCache';
import './UserAvatar.css';

/**
 * Reusable avatar component that resolves user avatar from userInfoCache.
 * - Sync cache read on init (no flash if cache is warm)
 * - Async fetch fallback if not cached
 * - Falls back to first-letter initials
 */
const UserAvatar = ({ userId, username, avatarUrl: providedAvatarUrl = null, size = 22, className = '' }) => {
  const cached = getCachedUserInfo(userId);
  const [avatarUrl, setAvatarUrl] = useState(providedAvatarUrl || cached?.avatar_url || null);
  const initials = (username || cached?.username || 'U')[0].toUpperCase();

  useEffect(() => {
    if (providedAvatarUrl) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- mirrors the caller-provided avatar URL onto local state used by the fallback paths below
      setAvatarUrl(providedAvatarUrl);
      return undefined;
    }
    if (!userId) {
      setAvatarUrl(null);
      return undefined;
    }

    const cachedAvatarUrl = getCachedUserInfo(userId)?.avatar_url || null;
    if (cachedAvatarUrl) {
      setAvatarUrl(cachedAvatarUrl);
      return undefined;
    }

    setAvatarUrl(null);
    let cancelled = false;
    getUserInfo(userId).then(info => {
      if (!cancelled) setAvatarUrl(info?.avatar_url || null);
    });
    return () => { cancelled = true; };
  }, [providedAvatarUrl, userId]);

  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt=""
        className={`user-avatar-img ${className}`}
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <span
      className={`user-avatar-initials ${className}`}
      title={username}
      style={{ width: size, height: size, fontSize: size * 0.45 }}
    >
      {initials}
    </span>
  );
};

export default UserAvatar;
